import React, { useEffect, useState, useRef } from "react";
import { Filter, Check, Trash2, Search, KeyRound, X, Copy } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  clearError,
  deleteMultipleUsers,
} from "../../store/slices/userSlice";
import { withVersion } from "../../utils/concurrency";
import { CreateUserData, User, UserFilters } from "../../types";
import Button from "../../components/UI/Button";
import { Modal } from "../../components/UI/Modal";
import { UserForm } from "../../components/users/UserForm";
import { UserTable } from "../../components/users/UserTable";
import { Pagination } from "../../components/ListView/Pagination";
import { ChevronDown } from "lucide-react";
import { UserFilters as Filters } from "../../components/users/UserFilters";
import { Input } from "../../components/UI/FormField";
import { driverPinAPI } from "../../services/api";

export const DriversList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("users");

  const {
    users,
    loading,
    error,
    pagination: { total, page, pages },
  } = useAppSelector((state) => state.users);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<UserFilters>({
    search: "",
    page: 1,
    limit: 20,
    status: "",
    role: "driver",
  });

  /**
   * How many drivers on this page still cannot sign in on a truck.
   *
   * Every driver created before truck devices existed has no PIN, and no PIN
   * means no way onto a truck's screen — so this is the migration this page has
   * to make visible. It counts the loaded page rather than the whole fleet
   * because that is what is on screen; the bulk action itself covers everybody.
   */
  const withoutPin = users.filter(
    (user) => user.role === "driver" && !user.hasDriverPin,
  ).length;

  const [pinBusy, setPinBusy] = useState(false);

  /**
   * The PIN currently on screen, if an admin has asked to see one.
   *
   * Held in state and nowhere else — never written to storage, never put in a
   * URL, and dropped the moment the dialog closes. A credential being read aloud
   * over the phone should exist for the length of that call and no longer.
   */
  const [revealed, setRevealed] = useState<{ name: string; pin: string } | null>(
    null,
  );

  /**
   * Email a driver the PIN they already have.
   *
   * Not the same act as issuing one, and the difference is why it has its own
   * menu entry: nothing changes, so a PIN the driver had written down still
   * works afterwards. The server falls back to issuing when there is nothing
   * readable to resend and says so, which is why the toast comes from the
   * response rather than being written here.
   */
  const handleSendPin = async (user: User) => {
    setPinBusy(true);
    try {
      const res = await driverPinAPI.send(user._id);
      toast.success(res.data.message);
      dispatch(getUsers(filters));
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || t("driversList.pin.sendFailed"),
      );
    } finally {
      setPinBusy(false);
    }
  };

  /**
   * A PIN that cannot be shown, and why — with the fix attached when there is
   * one.
   */
  const [pinBlocked, setPinBlocked] = useState<{
    user: User;
    message: string;
    /** True only for the one cause an admin can clear from here. */
    fixable: boolean;
  } | null>(null);

  /** Read a driver's PIN back, for the one on the phone who has forgotten it. */
  const handleViewPin = async (user: User) => {
    setPinBusy(true);
    try {
      const res = await driverPinAPI.reveal(user._id);
      setRevealed({ name: user.fullName, pin: res.data.data.pin });
    } catch (error: any) {
      const code = error.response?.data?.code;
      const message =
        error.response?.data?.message || t("driversList.pin.viewFailed");

      /**
       * Only one of the three reasons has an answer on this screen.
       *
       *   PIN_NOT_VIEWABLE   this PIN predates readable copies. A new one will
       *                      be viewable, and issuing it is one click — so the
       *                      dialog offers it rather than describing it.
       *   PIN_VIEW_DISABLED  the deployment has no encryption key. Nothing an
       *                      admin can do per-driver; issuing a new PIN would
       *                      change the driver's credential and still not show
       *                      it, which is strictly worse than doing nothing.
       *   PIN_NOT_ISSUED     no PIN at all — "Send truck PIN by email" is right
       *                      there in the same menu.
       */
      if (code === "PIN_NOT_VIEWABLE") {
        setPinBlocked({ user, message, fixable: true });
      } else if (code === "PIN_VIEW_DISABLED") {
        setPinBlocked({ user, message, fixable: false });
      } else {
        toast.error(message);
      }
    } finally {
      setPinBusy(false);
    }
  };

  /**
   * Issue a new PIN and show it, in one step.
   *
   * The way out of "this PIN cannot be shown". Two calls rather than a new
   * endpoint that returns a PIN on creation: issuing already emails the driver,
   * and the reveal that follows goes through the same admin-only, audit-logged
   * route as any other. Nothing new about how a PIN can leave the server.
   */
  const handleIssueAndShow = async (user: User) => {
    setPinBusy(true);
    try {
      await driverPinAPI.reissue(user._id);
      const res = await driverPinAPI.reveal(user._id);

      setPinBlocked(null);
      setRevealed({ name: user.fullName, pin: res.data.data.pin });
      dispatch(getUsers(filters));
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || t("driversList.pin.viewFailed"),
      );
    } finally {
      setPinBusy(false);
    }
  };

  /**
   * Issue one driver a PIN and email it.
   *
   * Confirmed when they already have one, and not when they do not. The two are
   * different acts: giving a credential to somebody who has none is additive,
   * and replacing a working one takes effect immediately — a driver mid-shift
   * keeps their session, but the number they have written down stops working.
   */
  const handleIssuePin = async (user: User) => {
    if (!window.confirm(t("driversList.pin.confirmReissue", { name: user.fullName }))) {
      return;
    }

    setPinBusy(true);
    try {
      const res = await driverPinAPI.reissue(user._id);
      toast.success(res.data.message || t("driversList.pin.sent"));
      // Refetch so the column reflects the new answer rather than the one the
      // page loaded with.
      dispatch(getUsers(filters));
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || t("driversList.pin.sendFailed"),
      );
    } finally {
      setPinBusy(false);
    }
  };

  /** Fill the gap for every driver who has never had a PIN. */
  const handleIssueMissingPins = async () => {
    if (!window.confirm(t("driversList.pin.confirmBulk", { count: withoutPin }))) {
      return;
    }

    setPinBusy(true);
    try {
      const res = await driverPinAPI.issueMissing();
      const { failed } = res.data.data;

      // A partial failure is the expected outcome, so it is reported as a
      // warning with the count rather than as a success or as an error: some
      // drivers now have PINs and some still need the button pressed again.
      if (failed.length) {
        toast.warning(res.data.message);
      } else {
        toast.success(res.data.message);
      }

      dispatch(getUsers(filters));
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || t("driversList.pin.bulkFailed"),
      );
    } finally {
      setPinBusy(false);
    }
  };

  const prevSearch = useRef(filters.search);

  useEffect(() => {
    const searchChanged = prevSearch.current !== filters.search;
    prevSearch.current = filters.search;

    const params = {
      search: filters.search,
      page: filters.page,
      limit: filters.limit,
      status: filters.status,
      role: filters.role,
    };

    if (searchChanged) {
      if (filters.search === "") {
        dispatch(getUsers(params));
      } else {
        const timer = setTimeout(() => dispatch(getUsers(params)), 750);
        return () => clearTimeout(timer);
      }
      return;
    }
    dispatch(getUsers(params));
  }, [dispatch, filters]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleFiltersChange = (newFilters: Partial<UserFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: newFilters.page || 1,
    }));
    setSelectedIds([]);
  };

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(users.map((user) => user._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
    }
  };

  const handleCreateUser = async (data: CreateUserData) => {
    try {
      await dispatch(createUser(data)).unwrap();
      toast.success(t("driversList.toast.createSuccess"));
      setIsModalOpen(false);
    } catch (error) {}
  };

  const handleUpdateUser = async (data: Partial<User>) => {
    if (!editingUser) return;

    try {
      await dispatch(
        updateUser({
          id: editingUser._id,
          userData: withVersion(data, editingUser),
        }),
      ).unwrap();
      toast.success(t("driversList.toast.updateSuccess"));
      setIsModalOpen(false);
      setEditingUser(null);
    } catch (error) {
      if (typeof error === "string") toast.error(error);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t("driversList.confirm.delete"))) {
      try {
        await dispatch(deleteUser(id)).unwrap();
        toast.success(t("driversList.toast.deleteSuccess"));
        setSelectedIds((prev) =>
          prev.filter((selectedId) => selectedId !== id),
        );
      } catch (error) {}
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    if (
      window.confirm(
        t("driversList.confirm.bulkDelete", { count: selectedIds.length }),
      )
    ) {
      try {
        await dispatch(deleteMultipleUsers(selectedIds)).unwrap();
        toast.success(
          t("driversList.toast.bulkDeleteSuccess", {
            count: selectedIds.length,
          }),
        );
        setSelectedIds([]);
      } catch (error) {}
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const pagination = {
    page,
    pages,
    total,
    hasNext: page < pages,
    hasPrev: page > 1,
  };

  return (
    <div>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-dark-900 mb-4">
            {t("driversList.page.title")}
          </h1>

          <div className="flex items-center justify-between gap-4 max-sm:flex-wrap">
            <div className="flex-1 min-w-[250px] max-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder={t("driversList.controls.searchPlaceholder")}
                  value={filters.search}
                  onChange={(e) =>
                    handleFiltersChange({ search: e.target.value })
                  }
                  className="pl-10 bg-white"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="w-4 h-4" />
                  <span>{t("driversList.controls.filter")}</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>

                {showFilters && (
                  <div className="absolute right-0 max-sm:left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10">
                    <Filters
                      filters={filters}
                      setShowFilters={setShowFilters}
                      onFiltersChange={handleFiltersChange}
                      onClearFilters={() =>
                        setFilters({
                          search: "",
                          page: 1,
                          limit: 20,
                          status: "",
                          role: "driver",
                        })
                      }
                    />
                  </div>
                )}
              </div>

              <Button onClick={() => setIsModalOpen(true)} variant="primary">
                {t("driversList.controls.addDriver")}
              </Button>
            </div>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Check className="h-5 w-5 text-blue-600" />
              <span className="text-blue-800 font-medium">
                {t("selection.itemsSelected", {
                  count: selectedIds.length,
                  plural: selectedIds.length !== 1 ? "s" : "",
                })}
              </span>
            </div>
            <button
              onClick={handleBulkDelete}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              <span>{t("selection.deleteSelected")}</span>
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/*
            The migration prompt, shown only while there is something to migrate.

            It disappears on its own once every driver has a PIN, which is the
            right shape for a banner about a one-time transition — a permanent
            button for a job that is done is a button people learn to ignore.
          */}
          {withoutPin > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-sm text-amber-900">
                {t("driversList.pin.banner", { count: withoutPin })}
              </p>
              <Button
                variant="secondary"
                onClick={handleIssueMissingPins}
                disabled={pinBusy}
              >
                <KeyRound className="w-4 h-4 mr-1.5" />
                {t("driversList.pin.sendAll")}
              </Button>
            </div>
          )}

          <UserTable
            users={users}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelectOne={handleSelectOne}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onSendPin={handleSendPin}
            onViewPin={handleViewPin}
            onIssuePin={handleIssuePin}
            loading={loading}
          />
          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            pageSize={filters.limit}
          />
        </div>
      </div>

      {/* ── Why a PIN cannot be shown, and the one-click fix ───────────────
          Reached instead of the reveal dialog. Separate from a toast because
          one of the two cases has an action attached, and an action does not
          belong in something that dismisses itself. */}
      {pinBlocked && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="font-semibold text-gray-900">
              {t("driversList.pin.blockedTitle", {
                name: pinBlocked.user.fullName,
              })}
            </h2>
            <p className="mt-2 text-sm text-gray-500">{pinBlocked.message}</p>

            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => setPinBlocked(null)}
                className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                {t("driversList.pin.close")}
              </button>
              {pinBlocked.fixable && (
                <button
                  type="button"
                  disabled={pinBusy}
                  onClick={() => handleIssueAndShow(pinBlocked.user)}
                  className="flex-1 rounded-xl bg-gray-900 py-2.5 text-sm font-medium text-white disabled:opacity-50"
                >
                  {t("driversList.pin.issueAndShow")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── A PIN on screen ─────────────────────────────────────────────────
          For the driver on the phone who has forgotten theirs. Deliberately
          plain: no copy-to-clipboard by default, no persistence, and it closes
          to nothing — the value is in state and vanishes with the dialog. */}
      {revealed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
            <div className="mb-1 flex items-start justify-between">
              <span />
              <button
                type="button"
                onClick={() => setRevealed(null)}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"
                aria-label={t("driversList.pin.close")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <h2 className="font-semibold text-gray-900">
              {t("driversList.pin.viewTitle", { name: revealed.name })}
            </h2>

            <p className="my-6 select-all font-mono text-4xl font-bold tracking-[0.3em] text-gray-900">
              {revealed.pin}
            </p>

            <p className="mb-5 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {t("driversList.pin.viewWarning")}
            </p>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(revealed.pin);
                    toast.success(t("driversList.pin.copied"));
                  } catch {
                    // Clipboard access is refused on insecure origins and in
                    // locked-down browsers. The PIN is on screen regardless, so
                    // this is a convenience failing, not the task.
                    toast.info(t("driversList.pin.copyManually"));
                  }
                }}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                <Copy className="h-4 w-4" />
                {t("driversList.pin.copy")}
              </button>
              <button
                type="button"
                onClick={() => setRevealed(null)}
                className="flex-1 rounded-xl bg-gray-900 py-2.5 text-sm font-medium text-white"
              >
                {t("driversList.pin.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={
          editingUser
            ? t("driversList.modal.editTitle", {
                id: editingUser._id.slice(-6).toUpperCase(),
              })
            : t("driversList.modal.createTitle")
        }
        size="xl"
      >
        <div className="flex-1 overflow-y-auto">
          <UserForm
            user={editingUser || undefined}
            onSubmit={(data) => {
              if (editingUser) {
                return handleUpdateUser(data);
              } else {
                return handleCreateUser(data as CreateUserData);
              }
            }}
            loading={loading}
            isEditMode={!!editingUser}
            isDriver={true}
          />
        </div>
      </Modal>
    </div>
  );
};
